import type { AxiosError } from 'axios';
import { isAxiosError } from 'axios';

import type { ApiErrorType, ServerError } from 'models';
import { handleSnackbarError, useSnackbarStore } from 'stores/zustand/snackbarStore';
import { getErrorMessage } from 'utils/errors';

import { parseError } from './parseError';

const DEFAULT_ERROR_MESSAGE = 'Something went wrong, please try again.';

export type ErrorNotification =
    | { key: string; message: string; serverError: ServerError; type: 'server' }
    | { key: string; message: string; type: 'client' };

export function getErrorNotification(
    error: unknown,
    options: { fallbackMessage?: string } = {},
): ErrorNotification | null {
    if (!error) return null;

    if (isAxiosError<ApiErrorType>(error)) {
        const serverError = parseError(error as AxiosError<ApiErrorType>);
        const message = getErrorMessage(serverError);
        return { key: `server:${message}`, message, serverError, type: 'server' };
    }

    const message = error instanceof Error ? error.message : (options.fallbackMessage ?? DEFAULT_ERROR_MESSAGE);
    return { key: `client:${message}`, message, type: 'client' };
}

export function showErrorNotification(notification: ErrorNotification): void {
    if (notification.type === 'server') {
        handleSnackbarError(notification.serverError);
        return;
    }

    useSnackbarStore.getState().actions.pushMessage({ text: notification.message, variant: 'negative' });
}
